import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tooltip,
  Checkbox,
  Drawer
} from '@mui/material';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import Settings from '@mui/icons-material/Settings';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Table, TableBody, TableRow, TableCell, } from '@mui/material';
import { AutocompleteDropdown } from './AutocompleteDropdown';
import { useApiClient } from '../../config/axios';
import { AxiosInstance } from 'axios';

// Helper function to determine entity types based on column
const getEntityTypesForColumn = (columnKey: string): string[] => {
  switch (columnKey) {
    case 'data_index':
      return ['attribute', 'level', 'measure', 'member', 'base_metrics', 'computed_metric'];
    case 'xtype':
      return ['xtype'];
    case 'dimension':
      return ['dimension'];
    case 'renderer':
      return ['renderer'];
    case 'editor':
      return ['editor'];
    case 'text':
    case 'label':
    case 'label_text':
    case 'display_text':
      return ['attribute', 'level', 'measure', 'member', 'base_metrics', 'computed_metric'];
    default:
      return ['attribute', 'level', 'measure'];
  }
};

/**
 * Generate a UUID v4
 * This is a simple implementation that generates valid UUIDs for PostgreSQL
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Auto-populate related fields based on data_index selection
const autoPopulateRelatedFields = async (apiClient: AxiosInstance, rows: any[], rowId: string, dataIndex: string, sourceField: 'data_index' | 'text', onDataChange: (newRows: any[]) => void) => {
  if (!dataIndex) return;

  try {
    console.log(`AUTO-POPULATE: Fetching related fields for data_index: ${dataIndex} (source: ${sourceField})`);
    const response = await apiClient.get('/dropdown/related-fields', {
      params: { data_index: dataIndex }
    });
    const data = response.data;

    if (data.success && data.fields) {
      const newRows = rows.map(row => {
        if (row.item_id === rowId) {
          console.log(`AUTO-POPULATE: Processing row ${rowId}, current label_text: "${row.label_text}"`);
          const updatedRow = { ...row };

          // Update related fields based on what was selected
          if (sourceField === 'data_index') {
            // User selected data_index, so update text/label fields and renderer
            // DON'T update data_index field itself to prevent reset
            if (data.fields.text) {
              console.log(`AUTO-POPULATE: Setting text to '${data.fields.text}' for row ${rowId}`);
              // Try to find the correct text field name in the row
              if ('text' in updatedRow) updatedRow.text = data.fields.text;
              else if ('label' in updatedRow) updatedRow.label = data.fields.text;
              else if ('label_text' in updatedRow) updatedRow.label_text = data.fields.text;
              else if ('display_text' in updatedRow) updatedRow.display_text = data.fields.text;
            }
            if (data.fields.renderer) {
              console.log(`AUTO-POPULATE: Setting renderer to '${data.fields.renderer}' for row ${rowId}`);
              updatedRow.renderer = data.fields.renderer;
            }
            // Populate formula fields if available
            if (data.fields.formula) {
              console.log(`AUTO-POPULATE: Setting formula to '${data.fields.formula}' for row ${rowId}`);
              if ('formula' in updatedRow) updatedRow.formula = data.fields.formula;
              if ('Formula' in updatedRow) updatedRow.Formula = data.fields.formula;
            }
            if (data.fields.calculation) {
              console.log(`AUTO-POPULATE: Setting calculation in extra_props for row ${rowId}`);
              const extraProps = updatedRow.extra_props ? JSON.parse(updatedRow.extra_props) : {};
              extraProps.calculation = data.fields.calculation;
              updatedRow.extra_props = JSON.stringify(extraProps);
            }
            // Preserve the original data_index value that was selected
            console.log(`AUTO-POPULATE: Preserving data_index value '${row.data_index}' for row ${rowId}`);
          } else if (sourceField === 'text') {
            // User selected text, so update data_index and renderer
            // DON'T update the text field itself - the user already chose the correct text value
            if (data.fields.dataIndex || data.fields.data_index) {
              const dataIndexValue = data.fields.dataIndex || data.fields.data_index;
              console.log(`AUTO-POPULATE: Setting data_index to '${dataIndexValue}' for row ${rowId}`);
              updatedRow.data_index = dataIndexValue;
            }
            if (data.fields.renderer) {
              console.log(`AUTO-POPULATE: Setting renderer to '${data.fields.renderer}' for row ${rowId}`);
              updatedRow.renderer = data.fields.renderer;
            }
            // Populate formula fields if available
            if (data.fields.formula) {
              console.log(`AUTO-POPULATE: Setting formula to '${data.fields.formula}' for row ${rowId}`);
              if ('formula' in updatedRow) updatedRow.formula = data.fields.formula;
              if ('Formula' in updatedRow) updatedRow.Formula = data.fields.formula;
            }
            if (data.fields.calculation) {
              console.log(`AUTO-POPULATE: Setting calculation in extra_props for row ${rowId}`);
              const extraProps = updatedRow.extra_props ? JSON.parse(updatedRow.extra_props) : {};
              extraProps.calculation = data.fields.calculation;
              updatedRow.extra_props = JSON.stringify(extraProps);
            }
            // Preserve the text field that was just selected by the user
            console.log(`AUTO-POPULATE: Preserving user-selected text value for row ${rowId}`);
            console.log(`AUTO-POPULATE: Current text field values - text: "${updatedRow.text}", label: "${updatedRow.label}", label_text: "${updatedRow.label_text}", display_text: "${updatedRow.display_text}"`);
          }

          return updatedRow;
        }
        return row;
      });

      onDataChange(newRows);
    } else {
      console.log(`AUTO-POPULATE: No related fields found for data_index: ${dataIndex}`);
    }
  } catch (error) {
    console.error('AUTO-POPULATE ERROR: Failed to get related fields:', error);
  }
};

/**  
 * A standalone editor for simple (primitive) values  
 * that only commits on Enter, without breaking hook rules.  
 */
function PrimitiveEditor({ name, value, onChange }) {
  const [localValue, setLocalValue] = useState(String(value));
  useEffect(() => {
    setLocalValue(String(value));
  }, [value]);

  return (
    <TableRow>
      <TableCell component="th" sx={{ pr: 2 }}>{name}</TableCell>
      <TableCell>
        <TextField
          fullWidth
          variant="standard"
          value={localValue}
          onChange={e => setLocalValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onChange(name, localValue);
            }
          }}
        />
      </TableCell>
    </TableRow>
  );
}

/** 
 * Recursively render any JS value: 
 * - primitives as TextField 
 * - plain objects as key/value accordion 
 * - arrays of primitives as list rows 
 * - arrays of objects as per‑item accordions 
 */
function RenderValue({ name, value, onChange }) {
  // console.log("RenderValue", name, value);
  // 1) primitives
  // if (value == null || typeof value !== 'object') {
  //   return (
  //     <TableRow>
  //       <TableCell component="th" sx={{ pr:2 }}>{name}</TableCell>
  //       <TableCell>
  //         <TextField
  //           fullWidth
  //           variant="standard"
  //           value={String(value)}
  //           onChange={e => onChange(name, e.target.value)}
  //         />
  //       </TableCell>
  //     </TableRow>
  //   );
  // }

  // 1) primitives (local state + commit on Enter)
  if (value == null || typeof value !== 'object') {
    return <PrimitiveEditor name={name} value={value} onChange={onChange} />;
  }

  // 2) array of primitives → render an editable TextField for each entry
  if (Array.isArray(value) && value.every(v => typeof v !== 'object')) {
    return (
      <TableRow>
        <TableCell component="th">{name}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {value.map((v, i) => (
              <TextField
                key={i}
                size="small"
                variant="standard"
                fullWidth
                label={`${name}[${i}]`}
                value={v}
                onChange={e => {
                  const copy = [...value];
                  copy[i] = e.target.value;
                  onChange(name, copy);
                }}
              />
            ))}
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  // 3) array of objects → one accordion per item
  if (Array.isArray(value) && value.every(v => typeof v === 'object')) {
    return (
      <TableRow>
        <TableCell colSpan={2} sx={{ p: 0 }}>
          <Accordion sx={{ display: 'block', mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{name}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableBody>
                  {value.map((item, idx) => (
                    <React.Fragment key={idx}>
                      <TableRow>
                        <TableCell colSpan={2} sx={{ p: 0 }}>
                          <Accordion sx={{ display: 'block', mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography>{name} #{idx + 1}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Table size="small">
                                <TableBody>
                                  {Object.entries(item).map(([k, v]) => (
                                    <RenderValue
                                      key={k}
                                      name={k}
                                      value={v}
                                      onChange={(field, newVal) => {
                                        const copy = [...value];
                                        copy[idx] = { ...copy[idx], [field]: newVal };
                                        onChange(name, copy);
                                      }}
                                    />
                                  ))}
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        </TableCell>
      </TableRow>
    );
  }

  // 4) plain object → single accordion
  return (
    <TableRow>
      <TableCell colSpan={2} sx={{ p: 0 }}>
        <Accordion sx={{ display: 'block', mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{name}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableBody>
                {Object.entries(value).map(([k, v]) => (
                  <RenderValue
                    key={k}
                    name={k}
                    value={v}
                    onChange={(field, newVal) => {
                      const updated = { ...value, [field]: newVal };
                      onChange(name, updated);
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      </TableCell>
    </TableRow>
  );
}


/**
 * Format data_index based on source_type from catalog
 * For attribute, level, member: "{source_type}:{dataindex}:name"
 * For base_metric, computed_metric: keep as-is
 */
function formatDataIndex(dataIndex, catalog) {
  if (!dataIndex) return dataIndex;

  const meta = catalog[dataIndex] || {};
  const sourceType = meta.source_type;

  // Check if concatenation is needed
  if (sourceType === 'attribute' || sourceType === 'level' || sourceType === 'member') {
    return `${sourceType}:${dataIndex}:name`;
  }

  // For base_metric, computed_metric, or unknown types, keep as-is
  return dataIndex;
}

/**
 * Extract the original data_index from a potentially formatted value
 * e.g., "attribute:customer_id:name" -> "customer_id"
 */
function extractOriginalDataIndex(formattedValue) {
  if (!formattedValue) return formattedValue;

  // Check if it's a formatted value (has the pattern type:value:name)
  const parts = formattedValue.split(':');
  if (parts.length === 3 && parts[2] === 'name') {
    // It's formatted, return the middle part
    return parts[1];
  }

  // Not formatted, return as-is
  return formattedValue;
}

function hydrateFromCatalog(row, catalog) {
  const meta = catalog[row.data_index] || {};

  // Start with the current row (including its existing extra_props)
  const result = { ...row };

  // IMPORTANT: Preserve existing extra_props - don't overwrite them
  const existingExtraProps = row.extra_props || {};

  // Copy fields from catalog EXCEPT extra_props and data_index
  Object.entries(meta).forEach(([k, v]) => {
    if (k === 'data_index' || k === 'extra_props') {
      // Skip data_index (already set) and extra_props (preserve existing)
      return;
    }
    // All other fields from catalog go directly to the row
    result[k] = v;
  });

  // Keep the existing extra_props (from the config)
  result.extra_props = existingExtraProps;

  return result;
}

/**
 * A single DynamicEditor that handles:
 * - a “props-only” accordion editor for rootProps
 * - a flat DataGrid for arrays of objects, with add/delete/move/add-child
 * - recurses into nested array/object for anything else
 */
export default function DynamicEditor({
  data,
  rootProps = {},
  onDataChange,
  onRootChange,
  onDirtyChange,
  tdOnlyMap,
  setTdOnlyMap,
  allowTdOnly = false,
  itemCatalog = {},
  itemCatalogKeys = [],
  isTopDown = false,
  recordMode = false,
  path = []
}) {
  const apiClient = useApiClient();
  const apiRef = useGridApiRef();
  const lastKeyPressedRef = useRef(null);

  // ─── State ──────────────────────────────────────────
  const [expanded, setExpanded] = useState({});    // parentId → bool
  const [rowForProps, setRowForProps] = useState(null); // which row's extra_props drawer is open
  const [duplicateWarnings, setDuplicateWarnings] = useState({}); // item_id → warning message
  const [clickedCell, setClickedCell] = useState<{ rowId: string, field: string } | null>(null);

  const rows = useMemo(() => Array.isArray(data) ? data : [], [data]);

  const isFlatRows = rows.length > 0 && typeof rows[0] === 'object';
  // console.log('🔍 DynamicEditor render:', { data, rows, isFlatRows, expanded });
  const propsOnlyMode = !isFlatRows && Object.keys(rootProps).length > 0;
  // Defensive: if keys weren’t passed, build them locally
  const keys = useMemo(
    () => itemCatalogKeys?.length ? itemCatalogKeys : Object.keys(itemCatalog || {}),
    [itemCatalogKeys, itemCatalog]
  );


  // ─── Utilities ─────────────────────────────────────
  // build a path key for TD-only checkboxes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getPathKey = useCallback(key => [...path, key].join('.'), [path]);

  // Build a map parentId→children 
  const childMap = useMemo(() => {
    const m = {};
    rows.forEach(r => {
      const pid = r.parent_item_id;
      if (pid) m[pid] = (m[pid] || []).concat(r);
    });
    return m;
  }, [rows]);

  // Build displayRows by walking roots and recursing only when expanded[id]===true
  const displayRows = useMemo(() => {
    const out = [];
    function walk(r, depth = 0) {
      out.push({ ...r, __depth: depth, __hasChildren: (childMap[r.item_id] || []).length > 0 });
      if (expanded[r.item_id]) {
        (childMap[r.item_id] || []).forEach(c => walk(c, depth + 1));
      }
    }
    rows.filter(r => !r.parent_item_id).forEach(r => walk(r));
    return out;
  }, [rows, childMap, expanded]);

  // ─── Handlers ──────────────────────────────────────

  // Called whenever any cell in the grid commits its change
  const handleCellEditCommit = useCallback(params => {
    console.log('✍️ handleCellEditCommit:', params);
    const { id, field, value } = params;
    const idx = rows.findIndex(r => r.item_id === id);
    if (idx < 0) return;
    // 1) clone & update the one row
    let updated = { ...rows[idx], [field]: value };
    // 2) if they changed data_index, hydrate the rest
    if (field === 'data_index') {
      updated = hydrateFromCatalog(updated, itemCatalog);
      delete updated._isNew;
    }
    // 3) notify parent
    const newData = [...rows];
    newData[idx] = updated;
    onDataChange(newData);
    onDirtyChange?.(newData, tdOnlyMap);
  }, [rows, onDataChange, onDirtyChange, itemCatalog, tdOnlyMap]);

  const processRowUpdate = useCallback((newRow, oldRow) => {
    // always apply your hydration, _isNew-clear, etc.
    let updated = { ...newRow };
    if (newRow.data_index !== oldRow.data_index) {
      updated = hydrateFromCatalog(updated, itemCatalog);
      delete updated._isNew;
    }

    // Push preview if user pressed Enter OR Tab (both should commit)
    if (lastKeyPressedRef.current === 'Enter' || lastKeyPressedRef.current === 'Tab') {
      const newRows = rows.map(r =>
        r.item_id === updated.item_id ? updated : r
      );
      onDataChange(newRows);
      onDirtyChange?.(newRows, tdOnlyMap);
    }
    return updated;
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap, itemCatalog]);

  // When user clicks “Add Row”: flush any in-flight edit, then append
  const handleAdd = useCallback(() => {
    // tiny timeout lets DataGrid finish its own commit
    setTimeout(() => {
      const keys = Object.keys(rows[0] || {});
      const newRec = Object.fromEntries(keys.map(k => [k, '']));
      newRec.item_id = generateUUID();           // ← assign proper UUID
      newRec.parent_item_id = null;             // ← top-level row
      (newRec as any)._isNew = true; // ← MARK AS NEW

      // Initialize fields with proper types
      newRec.seq_no = '0';
      newRec.width = null;
      newRec.pinned = null;
      newRec.role = 'leaf';  // Default role

      // Copy extra_props from an existing row (they all should have the same config extra_props)
      let sourceExtraProps = {};
      if (rows.length > 0) {
        // Try to find a row that has extra_props
        for (const row of rows) {
          if (row.extra_props && typeof row.extra_props === 'object' && Object.keys(row.extra_props).length > 0) {
            sourceExtraProps = row.extra_props;
            break;
          }
        }
      }
      newRec.extra_props = JSON.stringify(sourceExtraProps);  // ← Copy extra_props as JSON string

      const newRows = [...rows, newRec];
      onDataChange(newRows);
      onDirtyChange?.(newRows, tdOnlyMap);
    }, 0);
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap]);


  const handleAddChild = useCallback(parentId => {
    // 1) blur the active cell input so any pending data_index commit fires
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // 2) wait a tick for that commit to propagate, then append to the full rows[]
    setTimeout(() => {
      const keys = Object.keys(rows[0] || {});
      const child = Object.fromEntries(keys.map(k => [k, '']));
      child.parent_item_id = parentId;
      (child as any)._isNew = true;
      child.item_id = generateUUID();

      // Initialize fields with proper types
      child.seq_no = '0';
      child.width = null;
      child.pinned = null;
      child.role = 'leaf';  // Default role

      // Copy extra_props from parent row or any existing row
      let sourceExtraProps = {};
      const parentRow = rows.find(r => r.item_id === parentId);

      // Try parent first
      if (parentRow && parentRow.extra_props && typeof parentRow.extra_props === 'object' && Object.keys(parentRow.extra_props).length > 0) {
        sourceExtraProps = parentRow.extra_props;
      } else {
        // Fallback to finding any row with extra_props
        for (const row of rows) {
          if (row.extra_props && typeof row.extra_props === 'object' && Object.keys(row.extra_props).length > 0) {
            sourceExtraProps = row.extra_props;
            break;
          }
        }
      }
      child.extra_props = JSON.stringify(sourceExtraProps);  // ← Copy extra_props as JSON string

      const newRows = [...rows, child];
      onDataChange(newRows);
      onDirtyChange?.(newRows, tdOnlyMap);

      setExpanded(exp => ({ ...exp, [parentId]: true }));
    }, 0);
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap, setExpanded]);


  // Delete a row at index
  const handleDelete = useCallback(idx => {
    const deletedRow = rows[idx];
    const newRows = rows.filter((_, i) => i !== idx);

    // Clear any warning for the deleted row and recalculate warnings for remaining rows
    if (deletedRow && deletedRow.item_id) {
      setDuplicateWarnings(prev => {
        const updated = { ...prev };
        delete updated[deletedRow.item_id];

        // Recalculate warnings for remaining rows since a duplicate might have been removed
        Object.keys(updated).forEach(itemId => {
          const row = newRows.find(r => r.item_id === itemId);
          if (row && row.data_index) {
            const stillHasDuplicate = newRows.some(r =>
              r.item_id !== itemId && r.data_index === row.data_index
            );
            if (!stillHasDuplicate) {
              delete updated[itemId];
            }
          }
        });

        return updated;
      });
    }

    onDataChange(newRows);
    onDirtyChange?.(newRows, tdOnlyMap);
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap, setDuplicateWarnings]);

  // Move up/down
  const handleMoveUp = useCallback(idx => {
    if (idx <= 0) return;
    const arr = [...rows];
    const [item] = arr.splice(idx, 1);
    arr.splice(idx - 1, 0, item);
    onDataChange(arr);
    onDirtyChange?.(arr, tdOnlyMap);
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap]);
  const handleMoveDown = useCallback(idx => {
    if (idx >= rows.length - 1) return;
    const arr = [...rows];
    const [item] = arr.splice(idx, 1);
    arr.splice(idx + 1, 0, item);
    onDataChange(arr);
    onDirtyChange?.(arr, tdOnlyMap);
  }, [rows, onDataChange, onDirtyChange, tdOnlyMap]);

  // TD-Only checkbox toggles
  const handleTdOnlyChange = useCallback((id, checked) => {
    const key = getPathKey(id);
    setTdOnlyMap(m => ({ ...m, [key]: checked }));
  }, [getPathKey, setTdOnlyMap]);

  // Extra-props drawer handler
  const handleExtraPropChange = useCallback((row, newProps) => {
    const idx = rows.findIndex(r => r.item_id === row.item_id);
    if (idx < 0) return;
    const arr = [...rows];
    arr[idx] = { ...arr[idx], extra_props: newProps };
    onDataChange(arr);
    setRowForProps(r => r && r.item_id === row.item_id
      ? { ...r, extra_props: newProps }
      : r
    );
  }, [rows, onDataChange]);

  // ─── Columns ──────────────────────────────────────

  // Expander column: shows chevron only when __hasChildren
  const expanderCol = useMemo(() => ({
    field: '__expander',
    headerName: '',
    width: 80,
    cellClassName: 'expander-cell',
    sortable: false, filterable: false, disableColumnMenu: true,
    renderCell: params => {
      const { row } = params;
      // console.log('ExpanderCell.renderCell:', {
      //   rowId:       row.item_id,
      //   hasChildren: row.__hasChildren,
      //   expanded:    !!expanded[row.item_id]
      // });
      if (!row.__hasChildren) return null;
      const isOpen = !!expanded[row.item_id];
      return (
        <Box sx={{ ml: row.__depth * 2, overflow: 'visible' }}>
          <IconButton
            size="small"
            onClick={() => {
              // console.log('Toggling expand for', row.item_id, 'oldState=', expanded[row.item_id]);
              setExpanded(e => ({ ...e, [row.item_id]: !isOpen }));
            }}
          >
            {isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>
      );
    }
  }), [expanded]);

  // TD-Only column
  const tdOnlyCol = useMemo(() => ({
    field: '__td',
    headerName: 'TD Only',
    width: 90,
    cellClassName: 'td-only-cell',
    sortable: false, filterable: false, disableColumnMenu: true,
    renderCell: params => (
      <Checkbox
        size="small"
        checked={!!tdOnlyMap[getPathKey(params.id)]}
        onChange={e => handleTdOnlyChange(params.id, e.target.checked)}
      />
    )
  }), [tdOnlyMap, getPathKey, handleTdOnlyChange]);

  // Action column: move up/down, delete, add-child, extra-props
  const actionCol = useMemo(() => ({
    field: '__actions',
    headerName: 'Actions',
    width: 280,
    cellClassName: 'action-cell',
    sortable: false, filterable: false, disableColumnMenu: true,
    renderCell: params => {
      const idx = rows.findIndex(r => r.item_id === params.row.item_id);
      return (
        <>
          <Tooltip title="Move Up"><span><IconButton size="small" onClick={() => handleMoveUp(idx)} disabled={idx === 0}><ArrowUpwardIcon /></IconButton></span></Tooltip>
          <Tooltip title="Move Down"><span><IconButton size="small" onClick={() => handleMoveDown(idx)} disabled={idx === rows.length - 1}><ArrowDownwardIcon /></IconButton></span></Tooltip>
          <Tooltip title="Delete Row"><span><IconButton size="small" onClick={() => handleDelete(idx)}><DeleteIcon /></IconButton></span></Tooltip>
          <Tooltip title="Add Child"><IconButton size="small" onClick={() => handleAddChild(params.row.item_id)}><AddIcon /></IconButton></Tooltip>
          <Tooltip title="Edit Extra Props"><IconButton size="small" onClick={() => setRowForProps(params.row)}><Settings /></IconButton></Tooltip>
        </>
      );
    }
  }), [rows, handleMoveUp, handleMoveDown, handleDelete, handleAddChild]);

  // Flat columns: one per data field, plus Autocomplete for data_index
  const flatColumns = useMemo(() => {
    if (!isFlatRows) return [];
    return Object.keys(rows[0])
      .filter(k => ![
        'item_id', 'parent_item_id', 'extra_props',
        'created_at', 'created_by', 'updated_at', 'updated_by', 'view_name', 'seq_no', 'role'
      ].includes(k))
      .map(field => {
        const header = field.replace(/_/g, ' ').toUpperCase();
        // 1) build the shared column settings
        const common = {
          field,
          headerName: header,
          minWidth: 120,
          flex: 1,
          resizable: true,
          editable: true,
          sortable: true,
          hideable: true
        };
        // Enhanced data_index column with database-driven suggestions
        if (field === 'data_index') {
          return {
            ...common,
            minWidth: 250,                    // Wider default for data_index column
            flex: 2,                          // Give it more space
            editable: false,                 // never let the grid switch to its own editor
            cellClassName: 'edit-dropdown-cell',
            renderCell: ({ row }) => {
              return (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setClickedCell({ rowId: row.item_id, field: 'data_index' });
                  }}
                  sx={{
                    width: '100%',
                    position: 'relative',
                    ...(duplicateWarnings[row.item_id] && {
                      borderLeft: '3px solid',
                      borderLeftColor: 'warning.main',
                      pl: 1
                    })
                  }}
                >
                  <AutocompleteDropdown
                    value={row.data_index || ''}
                    onChange={(value) => {
                      // Handle when value is cleared/deleted
                      if (!value) {
                        // Clear warning
                        setDuplicateWarnings(prev => {
                          const updated = { ...prev };
                          delete updated[row.item_id];
                          return updated;
                        });

                        // Update row with empty data_index
                        let updated = { ...row, data_index: '' };

                        // Clear other fields that were populated from catalog
                        Object.keys(row).forEach(key => {
                          if (key !== 'item_id' && key !== 'parent_item_id' && key !== 'extra_props' && key !== '_isNew') {
                            updated[key] = '';
                          }
                        });

                        const newRows = rows.map(r =>
                          r.item_id === row.item_id ? updated : r
                        );
                        onDataChange(newRows);
                        onDirtyChange?.(newRows, tdOnlyMap);
                        return;
                      }

                      // For regular typing (not selection), update immediately
                      // Check for duplicates
                      const isDuplicate = rows.some(r =>
                        r.item_id !== row.item_id && r.data_index === value
                      );

                      // Update or clear warning
                      setDuplicateWarnings(prev => {
                        const updated = { ...prev };
                        if (isDuplicate && value) {
                          updated[row.item_id] = `Warning: "${value}" already exists in another row`;
                        } else {
                          delete updated[row.item_id];
                        }
                        return updated;
                      });
                    }}
                    onSelect={(value, metadata) => {
                      // Handle both data_index update and auto-population in one operation
                      console.log(`data_index onSelect: value="${value}", metadata:`, metadata);

                      // Check for duplicates with the selected value
                      const isDuplicate = rows.some(r =>
                        r.item_id !== row.item_id && r.data_index === value
                      );

                      // Update or clear warning
                      setDuplicateWarnings(prev => {
                        const updated = { ...prev };
                        if (isDuplicate && value) {
                          updated[row.item_id] = `Warning: "${value}" already exists in another row`;
                        } else {
                          delete updated[row.item_id];
                        }
                        return updated;
                      });

                      // Update the row with new data_index
                      let updated = { ...row, data_index: value };

                      // Remove _isNew flag if it exists
                      if (updated._isNew) {
                        delete updated._isNew;
                      }

                      // Ensure all fields have at least empty string values for editability
                      Object.keys(row).forEach(key => {
                        if (updated[key] === undefined || updated[key] === null) {
                          updated[key] = '';
                        }
                      });

                      const newRows = rows.map(r =>
                        r.item_id === row.item_id ? updated : r
                      );
                      onDataChange(newRows);
                      onDirtyChange?.(newRows, tdOnlyMap);

                      // Auto-populate related fields when a database suggestion is selected
                      if (metadata?.id) {
                        autoPopulateRelatedFields(apiClient, newRows, row.item_id, metadata.id, 'data_index', onDataChange);
                      }
                    }}
                    placeholder="Search data index..."
                    contextKey="data_index"
                    entityTypes={getEntityTypesForColumn('data_index')}
                  />
                  {row.data_index && clickedCell?.rowId === row.item_id && clickedCell?.field === 'data_index' && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const updated = { ...row, data_index: '' };
                        const newRows = rows.map(r =>
                          r.item_id === row.item_id ? updated : r
                        );
                        onDataChange(newRows);
                        onDirtyChange?.(newRows, tdOnlyMap);
                        // Clear any duplicate warning when clearing the field
                        setDuplicateWarnings(prev => {
                          const updated = { ...prev };
                          delete updated[row.item_id];
                          return updated;
                        });
                      }}
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 16,
                        height: 16,
                        bgcolor: 'rgba(0,0,0,0.1)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.2)' }
                      }}
                    >
                      <span style={{ fontSize: '10px', lineHeight: 1 }}>×</span>
                    </IconButton>
                  )}
                  {duplicateWarnings[row.item_id] && (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main', fontSize: '11px', mt: 0.5 }}>
                      <WarningAmberIcon fontSize="small" sx={{ mr: 0.5 }} />
                      {duplicateWarnings[row.item_id]}
                    </Box>
                  )}
                </Box>
              );
            }
          };
        }

        // Enhanced text field with database-driven suggestions (handles 'text', 'label', 'label_text', etc.)
        if (field === 'text' || field === 'label' || field === 'label_text' || field === 'display_text') {
          return {
            ...common,
            minWidth: 200,
            flex: 1.5,
            editable: false,
            cellClassName: 'edit-dropdown-cell',
            renderCell: ({ row }) => {
              return (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setClickedCell({ rowId: row.item_id, field: field });
                  }}
                  sx={{
                    width: '100%',
                    position: 'relative'
                  }}
                >
                  <AutocompleteDropdown
                    value={row[field] || ''}
                    onChange={(value) => {
                      console.log(`DEBUG DynamicEditor onChange: field="${field}", value="${value}"`);
                      const updated = { ...row, [field]: value };
                      const newRows = rows.map(r =>
                        r.item_id === row.item_id ? updated : r
                      );
                      console.log(`DEBUG DynamicEditor: Updated row [field]="${updated[field]}"`);
                      onDataChange(newRows);
                      onDirtyChange?.(newRows, tdOnlyMap);
                    }}
                    onSelect={(value, metadata) => {
                      // Auto-populate related fields when a database suggestion is selected
                      if (metadata?.id) {
                        const updatedRows = rows.map(r =>
                          r.item_id === row.item_id ? { ...r, [field]: value } : r
                        );
                        autoPopulateRelatedFields(apiClient, updatedRows, row.item_id, metadata.id, 'text', onDataChange);
                      }
                    }}
                    placeholder={`Search ${field.replace(/_/g, ' ')}...`}
                    contextKey="text"
                    entityTypes={getEntityTypesForColumn(field)}
                  />
                  {row[field] && clickedCell?.rowId === row.item_id && clickedCell?.field === field && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const updated = { ...row, [field]: '' };
                        const newRows = rows.map(r =>
                          r.item_id === row.item_id ? updated : r
                        );
                        onDataChange(newRows);
                        onDirtyChange?.(newRows, tdOnlyMap);
                      }}
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 16,
                        height: 16,
                        bgcolor: 'rgba(0,0,0,0.1)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.2)' }
                      }}
                    >
                      <span style={{ fontSize: '10px', lineHeight: 1 }}>×</span>
                    </IconButton>
                  )}
                </Box>
              );
            }
          };
        }

        // Enhanced renderer field with database-driven suggestions  
        if (field === 'renderer') {
          return {
            ...common,
            minWidth: 150,
            editable: false,
            cellClassName: 'edit-dropdown-cell',
            renderCell: ({ row }) => {
              return (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setClickedCell({ rowId: row.item_id, field: 'renderer' });
                  }}
                  sx={{
                    width: '100%',
                    position: 'relative'
                  }}
                >
                  <AutocompleteDropdown
                    value={row.renderer || ''}
                    onChange={(value) => {
                      const updated = { ...row, renderer: value };
                      const newRows = rows.map(r =>
                        r.item_id === row.item_id ? updated : r
                      );
                      onDataChange(newRows);
                      onDirtyChange?.(newRows, tdOnlyMap);
                    }}
                    placeholder="Search renderer..."
                    contextKey="renderer"
                    entityTypes={getEntityTypesForColumn('renderer')}
                  />
                  {row.renderer && clickedCell?.rowId === row.item_id && clickedCell?.field === 'renderer' && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const updated = { ...row, renderer: '' };
                        const newRows = rows.map(r =>
                          r.item_id === row.item_id ? updated : r
                        );
                        onDataChange(newRows);
                        onDirtyChange?.(newRows, tdOnlyMap);
                      }}
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 16,
                        height: 16,
                        bgcolor: 'rgba(0,0,0,0.1)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.2)' }
                      }}
                    >
                      <span style={{ fontSize: '10px', lineHeight: 1 }}>×</span>
                    </IconButton>
                  )}
                </Box>
              );
            }
          };
        }

        // Enhanced editor field with database-driven suggestions
        if (field === 'editor') {
          return {
            ...common,
            minWidth: 150,
            editable: false,
            cellClassName: 'edit-dropdown-cell',
            renderCell: ({ row }) => {
              return (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setClickedCell({ rowId: row.item_id, field: 'editor' });
                  }}
                  sx={{
                    width: '100%',
                    position: 'relative'
                  }}
                >
                  <AutocompleteDropdown
                    value={row.editor || ''}
                    onChange={(value) => {
                      const updated = { ...row, editor: value };
                      const newRows = rows.map(r =>
                        r.item_id === row.item_id ? updated : r
                      );
                      onDataChange(newRows);
                      onDirtyChange?.(newRows, tdOnlyMap);
                    }}
                    placeholder="Search editor..."
                    contextKey="editor"
                    entityTypes={getEntityTypesForColumn('editor')}
                  />
                  {row.editor && clickedCell?.rowId === row.item_id && clickedCell?.field === 'editor' && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const updated = { ...row, editor: '' };
                        const newRows = rows.map(r =>
                          r.item_id === row.item_id ? updated : r
                        );
                        onDataChange(newRows);
                        onDirtyChange?.(newRows, tdOnlyMap);
                      }}
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 16,
                        height: 16,
                        bgcolor: 'rgba(0,0,0,0.1)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.2)' }
                      }}
                    >
                      <span style={{ fontSize: '10px', lineHeight: 1 }}>×</span>
                    </IconButton>
                  )}
                </Box>
              );
            }
          };
        }

        // 3) otherwise, just return the common definition
        return common;
      });
  }, [isFlatRows, rows, duplicateWarnings, clickedCell?.rowId, clickedCell?.field, onDataChange, onDirtyChange, tdOnlyMap, apiClient]);

  //   console.table(
  //   '💠 flatColumns',
  //   flatColumns.map(c => ({
  //     field: c.field,
  //     hasRenderEdit: !!c.renderEditCell,
  //     hasRenderCell: !!c.renderCell,
  //     editable: c.editable,
  //     cellClass: c.cellClassName
  //   }))
  // );


  const columnsWithExpander = useMemo(() => {
    const cols = [expanderCol];
    if (allowTdOnly) cols.push(tdOnlyCol);
    return cols.concat(flatColumns as any, actionCol as any);
  }, [expanderCol, tdOnlyCol, flatColumns, actionCol, allowTdOnly]);

  //   console.log(
  //   '💠 columnsWithExpander',
  //   columnsWithExpander.map(c => c.field)
  // );


  // ─── RENDER ─────────────────────────────────────────
  // Props-only mode
  if (propsOnlyMode) {
    return (
      <Box sx={{ mb: 2, width: '100%' }}>
        <Table size="small">
          <TableBody>
            {Object.entries(rootProps).map(([k, v]) => (
              <RenderValue
                key={k}
                name={k}
                value={v}
                onChange={(name, val) => {
                  const np = { ...rootProps, [name]: val };
                  onDataChange(np);
                  onDirtyChange?.(data, tdOnlyMap, np);
                }}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  }

  // Flat/grid mode
  if (isFlatRows) {
    return (
      <>
        {Object.keys(rootProps).length > 0 && (
          <Box sx={{ mb: 2, width: '100%' }}>
            {/* Root props editor — one accordion per key/value */}
            <Table size="small">
              <TableBody>
                {Object.entries(rootProps).map(([propName, propVal]) => (
                  <RenderValue
                    key={propName}
                    name={propName}
                    value={propVal}
                    onChange={(field, newVal) => {
                      // Build a new rootProps object and fire back to the parent
                      const updated = { ...rootProps, [field]: newVal };
                      onRootChange(updated);
                      onDirtyChange?.(data, tdOnlyMap, updated);
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        <Box sx={{ width: '100%' }}>
          <Button
            variant="contained"
            size="small"
            sx={{ mb: 1 }}
            onClick={() => setTimeout(handleAdd, 0)}  // flush any in-flight edits
          >
            Add Row
          </Button>

          <DataGrid
            rows={displayRows}
            columns={columnsWithExpander}
            apiRef={apiRef}
            getRowId={r => r.item_id}
            editMode="cell"
            onCellKeyDown={(params, event) => {
              lastKeyPressedRef.current = event.key;
              // Enable Tab navigation with commit
              if (event.key === 'Tab' && !event.shiftKey) {
                event.preventDefault();

                // Check if current cell is in edit mode before trying to stop it
                const cellMode = apiRef.current.getCellMode(params.id, params.field);
                if (cellMode === 'edit') {
                  // Stop edit mode on current cell (this commits the value)
                  apiRef.current.stopCellEditMode({
                    id: params.id,
                    field: params.field
                  });
                }

                // Then move to next editable cell
                // Get only truly editable columns (exclude expander, actions, td-only, etc.)
                const editableColumns = columnsWithExpander.filter(col =>
                  col.field !== '__expander' &&
                  col.field !== '__actions' &&
                  col.field !== '__td' &&
                  col.field !== 'data_index' && // Skip data_index as it has custom editor
                  (col as any).editable !== false
                );

                const currentFieldIndex = columnsWithExpander.findIndex(col => col.field === params.field);
                // Find next editable column after current position
                let nextEditableCol = null;
                for (let i = currentFieldIndex + 1; i < columnsWithExpander.length; i++) {
                  const col = columnsWithExpander[i];
                  if (editableColumns.some(ec => ec.field === col.field)) {
                    nextEditableCol = col;
                    break;
                  }
                }

                if (nextEditableCol) {
                  setTimeout(() => {
                    apiRef.current.setCellFocus(params.id, nextEditableCol.field);
                    apiRef.current.startCellEditMode({ id: params.id, field: nextEditableCol.field });
                  }, 50); // Small delay to ensure commit completes
                } else {
                  // At end of row, move to first editable cell of next row
                  const currentRowIndex = displayRows.findIndex(r => r.item_id === params.id);
                  if (currentRowIndex < displayRows.length - 1) {
                    const nextRowId = displayRows[currentRowIndex + 1].item_id;
                    const firstEditableField = editableColumns[0]?.field;
                    if (firstEditableField) {
                      setTimeout(() => {
                        apiRef.current.setCellFocus(nextRowId, firstEditableField);
                        apiRef.current.startCellEditMode({ id: nextRowId, field: firstEditableField });
                      }, 50);
                    }
                  }
                }
              }

              // Handle Shift+Tab for backward navigation
              if (event.key === 'Tab' && event.shiftKey) {
                event.preventDefault();

                // Check if current cell is in edit mode before trying to stop it
                const cellMode = apiRef.current.getCellMode(params.id, params.field);
                if (cellMode === 'edit') {
                  // Stop edit mode on current cell (commits the value)
                  apiRef.current.stopCellEditMode({
                    id: params.id,
                    field: params.field
                  });
                }

                // Move to previous editable cell
                // Get only truly editable columns (same filter as forward tab)
                const editableColumns = columnsWithExpander.filter(col =>
                  col.field !== '__expander' &&
                  col.field !== '__actions' &&
                  col.field !== '__td' &&
                  col.field !== 'data_index' && // Skip data_index as it has custom editor
                  (col as any).editable !== false
                );

                const currentFieldIndex = columnsWithExpander.findIndex(col => col.field === params.field);
                // Find previous editable column before current position
                let prevEditableCol = null;
                for (let i = currentFieldIndex - 1; i >= 0; i--) {
                  const col = columnsWithExpander[i];
                  if (editableColumns.some(ec => ec.field === col.field)) {
                    prevEditableCol = col;
                    break;
                  }
                }

                if (prevEditableCol) {
                  setTimeout(() => {
                    apiRef.current.setCellFocus(params.id, prevEditableCol.field);
                    apiRef.current.startCellEditMode({ id: params.id, field: prevEditableCol.field });
                  }, 50);
                } else {
                  // At beginning of row, move to last editable cell of previous row
                  const currentRowIndex = displayRows.findIndex(r => r.item_id === params.id);
                  if (currentRowIndex > 0) {
                    const prevRowId = displayRows[currentRowIndex - 1].item_id;
                    const lastEditableField = editableColumns[editableColumns.length - 1]?.field;
                    if (lastEditableField) {
                      setTimeout(() => {
                        apiRef.current.setCellFocus(prevRowId, lastEditableField);
                        apiRef.current.startCellEditMode({ id: prevRowId, field: lastEditableField });
                      }, 50);
                    }
                  }
                }
              }
            }}
            // onCellEditCommit={handleCellEditCommit}
            // Prevent blur from committing: re-enter edit-mode on focus-out
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={err => console.error(err)}
            autoHeight={false}               // Use fixed height for better performance
            disableRowSelectionOnClick
            disableColumnResize={false}
            // experimentalFeatures={{ columnResizing: true }}
            // columnResizeMode="fit"
            sx={{
              height: '70vh',                // Fixed height with scroll
              // restore grid lines for cells
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(224,224,224,1)'
              },
              // Sticky headers with distinct styling
              '& .MuiDataGrid-columnHeaders': {
                position: 'sticky',
                top: 0,
                backgroundColor: 'background.paper',
                borderBottom: '2px solid rgba(224,224,224,1)',
                zIndex: 1
              },
              // Row hover effect
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover'
              },
              // Alternating row colors
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: 'grey.50'
              },
              // allow chevrons & action icons to overflow their cells
              '& .MuiDataGrid-cell.expander-cell': { overflow: 'visible !important' },
              '& .MuiDataGrid-cell.action-cell': { overflow: 'visible !important' },
              '& .edit-dropdown-cell': { overflow: 'visible !important' },
            }}
          />

          <Button
            variant="contained"
            size="small"
            sx={{ mt: 1 }}
            onClick={() => setTimeout(handleAdd, 0)}  // Same handler as top button
          >
            Add Row
          </Button>

          <Drawer
            anchor="right"
            open={!!rowForProps}
            onClose={() => setRowForProps(null)}
            sx={{ zIndex: 1400 }}
          >
            <Box sx={{ width: 400, p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Extra Props</Typography>
              <Table size="small">
                <TableBody>
                  {rowForProps && Object.entries(
                    typeof rowForProps.extra_props === 'string'
                      ? (() => {
                        try {
                          return JSON.parse(rowForProps.extra_props || '{}');
                        } catch (e) {
                          console.warn('Failed to parse extra_props JSON:', rowForProps.extra_props);
                          return {};
                        }
                      })()
                      : (rowForProps.extra_props || {})
                  ).map(([k, v]) => (
                    <RenderValue
                      key={k}
                      name={k}
                      value={v}
                      onChange={(field, newVal) => {
                        const updated = { ...(rowForProps.extra_props || {}), [field]: newVal };
                        handleExtraPropChange(rowForProps, updated);
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Drawer>
        </Box>
      </>
    );
  }

  // Fallback: nested array/object recordMode logic unchanged…
  // (copy your existing recordMode and object recursion here)
  return <RenderValue name={path[path.length - 1] || ''} value={data} onChange={() => { }} />;

}
